/**
 * @file Navbar.jsx
 * @description Barra de navegación con lógica de marca dinámica y tratamiento premium del logo.
 * @author JEMAprod-Tecnologia (2026)
 */

import { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import data from "../data/content.json";
import { NavLink, Link, useLocation } from "react-router-dom";
import QuotationCartButton from "./QuotationCartButton";
import { ResolveCmsIcon } from "./ResolveCmsIcon";
import { useCmsDoc } from "../hooks/useCmsDoc";
import { formatNavLink, normalizeNavigationDoc } from "../lib/navigationMenu";
import { logoImgStyle, logoUrl, mergeIdentity } from "../lib/identityAgdysa";
import { useHeroChromeIdle } from "./HeroIdleChrome";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isNavbarHidden, setIsNavbarHidden] = useState(false);
  const chromeIdle = useHeroChromeIdle();
  const location = useLocation();
  const lastScrollY = useRef(0);
  const { data: navCms } = useCmsDoc("site", "navigation", normalizeNavigationDoc({}));
  const { data: identityCms } = useCmsDoc("site", "identity", mergeIdentity(null, data.logos));
  const identity = mergeIdentity(identityCms, data.logos);
  const nav = normalizeNavigationDoc(navCms || {});

  const isLabsyso = useMemo(() => 
    location.pathname.toLowerCase().includes("labsyso"), 
    [location.pathname]
  );

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;

      if (currentY > lastScrollY.current + 15 && currentY > 100) {
        setIsNavbarHidden(true);
      } else if (currentY < lastScrollY.current - 15 || currentY <= 100) {
        setIsNavbarHidden(false);
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const theme = useMemo(() => {
    const agdysaLogo = logoUrl(identity.logos.agdysaHorizontal, data.logos.agdysa_logo);
    const labsysoLogo = logoUrl(identity.logos.labsysoHorizontal, data.logos.labsyso_logo);
    const logoAsset = isLabsyso ? identity.logos.labsysoHorizontal : identity.logos.agdysaHorizontal;
    return {
      logo: isLabsyso ? labsysoLogo : agdysaLogo,
      logoStyle: logoImgStyle(logoAsset),
      logoBg: logoAsset?.fondoVisible ? logoAsset.fondoColor : "transparent",
      brandName: isLabsyso ? "LABSYSO" : identity.acronimo || "AGDYSA",
      primaryText: isLabsyso ? "text-[#211c52]" : "text-brand-primary",
      primaryBg: isLabsyso ? "bg-[#211c52]" : "bg-brand-primary",
      accentBg: isLabsyso ? "bg-[#211c52]" : "bg-brand-accent",
      hoverText: isLabsyso ? "hover:text-[#211c52]" : "hover:text-brand-primary",
      mobileActive: isLabsyso ? "bg-[#211c52]/5 text-[#211c52]" : "bg-brand-soft text-brand-primary",
    };
  }, [isLabsyso, identity]);

  const formatLink = formatNavLink;
  const desktopItems = (nav.items || []).filter((item) => item.label !== "Cotizar");
  const mobileItems = (nav.items || []).filter((item) => item.showInMobileMenu !== false);

  return (
    <motion.header 
      initial={{ opacity: 0, y: -20 }}
      animate={{
        opacity: chromeIdle && !isOpen ? 0 : 1,
        y: (isNavbarHidden || chromeIdle) && !isOpen ? -80 : 0,
      }}
      transition={{ duration: chromeIdle ? 0.45 : 0.2, ease: "easeOut" }}
      data-menu-open={isOpen ? "true" : "false"}
      className={`site-header fixed top-0 w-full z-[900] bg-white/93 backdrop-blur-[8px] border-b ${
        chromeIdle && !isOpen ? "pointer-events-none" : ""
      } ${isLabsyso ? "border-[#211c52]/10" : "border-black/5"}`}
    >
      <div className="container mx-auto px-6 h-[70px] flex justify-between items-center">
        
        {/* CONTENEDOR DEL LOGO DINÁMICO */}
        <Link 
          to="/" 
          className="relative flex items-center h-full group"
          aria-label={`Ir al inicio de ${theme.brandName}`}
        >
          <div
            className="relative h-full max-w-[180px] xl:max-w-[220px] flex items-center rounded-lg"
            style={{ backgroundColor: theme.logoBg }}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={theme.logo} // Forzar re-renderizado animado al cambiar de logo
                src={theme.logo}
                alt={`Logo de ${theme.brandName}`}
                initial={{ opacity: 0, scale: 0.9, x: -10 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 1.1, x: 10 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="h-17 md:h-18 w-auto object-contain image-render-auto group-hover:scale-105 transition-transform duration-300"
                style={{
                  filter: theme.logoStyle?.filter || "drop-shadow(0 2px 4px rgba(0,0,0,0.05))",
                  paddingTop: theme.logoStyle?.paddingTop,
                  paddingBottom: theme.logoStyle?.paddingBottom,
                  paddingLeft: theme.logoStyle?.paddingLeft,
                  paddingRight: theme.logoStyle?.paddingRight,
                  imageRendering: "-webkit-optimize-contrast",
                }}
                onError={(e) => { 
                  e.target.src = "https://agdysa.com/wp-content/themes/Agyxorth/assets/images/logo-dark.png";
                }}
              />
            </AnimatePresence>
          </div>
        </Link>

        {/* NAVEGACIÓN DESKTOP — visible desde lg (1024px) para laptops a 100% */}
        <nav className="hidden lg:flex items-center gap-3 xl:gap-6 shrink min-w-0">
          {desktopItems.map((item) => {
            const targetPath = formatLink(item.link);
            return (
              <NavLink 
                key={item.id} 
                to={targetPath} 
                className={({ isActive }) => 
                  `relative text-[0.72rem] xl:text-[0.85rem] font-bold uppercase tracking-wider xl:tracking-widest whitespace-nowrap transition-colors duration-300 ${
                    isActive ? theme.primaryText : `text-brand-dark/70 ${theme.hoverText}`
                  }`
                }
              >
                {item.label}
                {location.pathname === targetPath && (
                  <motion.div 
                    layoutId="navUnderline"
                    className={`absolute -bottom-1 left-0 right-0 h-[2px] ${theme.accentBg} rounded-full`}
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </NavLink>
            );
          })}
          
          {nav.formation?.enabled !== false && (
            <a
              href={nav.formation.url}
              target="_blank"
              rel="noopener noreferrer"
              className="relative text-[0.72rem] xl:text-[0.85rem] font-bold uppercase tracking-wider xl:tracking-widest whitespace-nowrap transition-colors duration-300 text-brand-dark/70 hover:text-brand-primary"
            >
              {nav.formation?.label || "Formación"}
            </a>
          )}

          <QuotationCartButton
            buttonClassName={`relative ${theme.primaryBg} text-white px-5 xl:px-8 py-2.5 xl:py-3 rounded-full text-[0.7rem] xl:text-xs font-bold uppercase tracking-widest hover:brightness-110 hover:shadow-[0_10px_20px_rgba(0,0,0,0.1)] transition-all active:scale-95 shrink-0`}
          />
        </nav>

        {/* BOTÓN MENÚ MÓVIL */}
        <button
          className={`lg:hidden ${theme.primaryText} p-2 rounded-xl transition-colors hover:bg-gray-100`}
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* DROPDOWN MÓVIL */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }} 
            animate={{ height: "auto", opacity: 1 }} 
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-white border-t border-border overflow-hidden shadow-2xl"
          >
            <div className="flex flex-col py-6 px-6 gap-2">
              {mobileItems.map((item) => {
                const targetPath = formatLink(item.link);
                const isActive = location.pathname === targetPath;

                return (
                  <NavLink 
                    key={item.id} 
                    to={targetPath} 
                    onClick={() => setIsOpen(false)}
                    className={`group flex items-center justify-between px-6 py-4 rounded-2xl font-bold transition-all ${
                      isActive ? theme.mobileActive : "text-brand-dark hover:bg-gray-50"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span className="transition-transform duration-200 group-hover:scale-125 group-focus-visible:scale-125">
                        <ResolveCmsIcon
                          icon={item.icon}
                          size={18}
                          className="text-current"
                          alt={item.label}
                        />
                      </span>
                      <span className="transition-transform duration-200 origin-left group-hover:scale-110 group-focus-visible:scale-110">
                        {item.label}
                      </span>
                    </span>
                    {isActive && <div className={`w-2 h-2 rounded-full ${theme.accentBg} animate-pulse`} />}
                  </NavLink>
                );
              })}
              {nav.formation?.enabled !== false && (
                <a
                  href={nav.formation.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="group flex items-center justify-between px-6 py-4 rounded-2xl font-bold transition-all text-brand-dark hover:bg-gray-50 w-full text-left"
                >
                  <span className="flex items-center gap-3">
                    <span className="transition-transform duration-200 group-hover:scale-125 group-focus-visible:scale-125">
                      <ResolveCmsIcon
                        icon={nav.formation?.icon || "lucide:GraduationCap"}
                        size={18}
                        className="text-current"
                        alt={nav.formation?.label || "Formación"}
                      />
                    </span>
                    <span className="transition-transform duration-200 origin-left group-hover:scale-110 group-focus-visible:scale-110">
                      {nav.formation?.label || "Formación"}
                    </span>
                  </span>
                </a>
              )}

              <QuotationCartButton
                variant="mobile-menu"
                label="Solicitar Cotización"
                buttonClassName={`relative w-full mt-4 ${theme.primaryBg} text-white py-5 rounded-2xl text-center font-bold shadow-lg`}
                onBeforeOpen={() => setIsOpen(false)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.header>
  );
};

export default Navbar;